import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const STATUS_COLORS = {
  succeeded: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-700'
};

const ENROLLMENT_STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  payment_failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  completed: 'bg-blue-100 text-blue-700',
  pending_payment: 'bg-yellow-100 text-yellow-700'
};

const MyPayments = () => {
  const [payments, setPayments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [paymentsRes, enrollmentsRes] = await Promise.all([
          api.get('/payments/my'),
          api.get('/enroll/my')
        ]);
        setPayments(paymentsRes.data.payments || []);
        setEnrollments(enrollmentsRes.data.enrollments || []);
      } catch (err) {
        console.error('Failed to load payment data:', err.message);
        setError('Failed to load payment information');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Compute summary stats
  const succeededPayments = payments.filter(p => p.status === 'succeeded');
  const totalPaid = succeededPayments.reduce((sum, p) => sum + (p.totalCharged || 0), 0);

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const activeSubscriptions = activeEnrollments.filter(
    e => e.billingPlan?.frequency && e.billingPlan.frequency !== 'one_time'
  );

  // Find the nearest next payment date from subscriptions
  const nextPaymentDate = activeSubscriptions
    .map(e => e.currentPeriodEnd)
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b))[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatPeriod = (start, end) => {
    if (!start || !end) return '--';
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${new Date(start).toLocaleDateString('en-US', opts)} - ${new Date(end).toLocaleDateString('en-US', opts)}`;
  };

  const formatFrequency = (freq) => {
    const map = { monthly: '/mo', quarterly: '/qtr', semester: '/sem', annual: '/yr', one_time: '' };
    return map[freq] || '';
  };

  const formatEnrollmentStatus = (status) => {
    const map = {
      active: 'Active',
      payment_failed: 'Payment Failed',
      cancelled: 'Cancelled',
      completed: 'Completed',
      pending_payment: 'Pending'
    };
    return map[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span className="ml-3 text-gray-500">Loading payments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Payments</h1>

      {/* Active Courses */}
      {activeSubscriptions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Active Courses</h2>
          <div className="space-y-3">
            {activeSubscriptions.map(enrollment => (
              <div
                key={enrollment._id}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <div>
                  <h3 className="font-medium text-gray-900">{enrollment.course?.name || 'Course'}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    ${enrollment.billingPlan?.amount?.toFixed(2)}{formatFrequency(enrollment.billingPlan?.frequency)}
                    {enrollment.billingPlan?.name ? ` — ${enrollment.billingPlan.name}` : ''}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Current period: {formatPeriod(enrollment.currentPeriodStart, enrollment.currentPeriodEnd)}
                  </p>
                </div>
                <span className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-medium ${ENROLLMENT_STATUS_COLORS[enrollment.status] || 'bg-gray-100 text-gray-500'}`}>
                  {formatEnrollmentStatus(enrollment.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* One-time / non-subscription active enrollments */}
      {activeEnrollments.filter(e => !e.billingPlan?.frequency || e.billingPlan.frequency === 'one_time').length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Active Enrollments</h2>
          <div className="space-y-3">
            {activeEnrollments
              .filter(e => !e.billingPlan?.frequency || e.billingPlan.frequency === 'one_time')
              .map(enrollment => (
                <div
                  key={enrollment._id}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">{enrollment.course?.name || 'Course'}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      ${enrollment.billingPlan?.amount?.toFixed(2)} — One-time
                    </p>
                  </div>
                  <span className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-medium ${ENROLLMENT_STATUS_COLORS[enrollment.status] || 'bg-gray-100 text-gray-500'}`}>
                    {formatEnrollmentStatus(enrollment.status)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Payment History Table */}
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Payment History</h2>
      {payments.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-500">
          No payments yet
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Course</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Amount</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payments.map(p => (
                  <tr key={p._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">
                      {formatDate(p.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {p.course?.name || '--'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900 font-medium">
                      ${p.totalCharged?.toFixed(2) || p.amount?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-500'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 capitalize">
                      {p.type?.replace('_', ' ') || '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPayments;
